import { ACCESS_RESOURCE_TYPE_LABELS } from '@infras/shared-types';
import type { AccessResourceType } from '@infras/shared-types';
import type { TaskAccessView } from '../../lib/requestTypes';

import { Avatar as PersonAvatar } from '../Avatar';

/** Avatar initials from a staff email: "dennis.nguyen@optisigns.com" → "DN". */
export { initials } from '../../lib/requestChips';

/** The 22px `req-av` disc — photo when the person has one, else initials. */
export function Avatar({ email }: { email: string }) {
  return <PersonAvatar email={email} title={email} />;
}

/** A person — initials avatar + the email itself. We show the full address, not a
 *  display name: infras has no name directory, and the email IS the identity every
 *  other surface (JWT, app_roles, MCP) keys on. */
export function Person({ email, you, oneLine }: { email: string; you?: boolean; oneLine?: boolean }) {
  return (
    <span className="req-person">
      <Avatar email={email} />
      <span>
        {/* break before the domain, not mid-name, when a narrow rail has to wrap */}
        {!oneLine && email.includes('@') ? (
          <>
            {email.slice(0, email.indexOf('@'))}
            <wbr />
            {email.slice(email.indexOf('@'))}
          </>
        ) : (
          email
        )}
        {you && <span className="muted text-xs"> (you)</span>}
      </span>
    </span>
  );
}

/** System label for a resource type; unknown types fall back to the raw value. */
export function typeLabel(t: string): string {
  return ACCESS_RESOURCE_TYPE_LABELS[t as AccessResourceType] ?? t;
}

/**
 * The inventory page for a linked entity, or null when there is no page to go to.
 * entity_id is the inventory row id. Clusters and secrets route by name, and a
 * linked task's resource_name IS that row's name (resolved server-side at
 * filing). Repos route by GitLab project id, which the task does not carry, and
 * databases / droplets have no detail page — those stay plain text rather than
 * linking to a 404.
 */
export function entityHref(r: Pick<TaskAccessView, 'entityId' | 'resourceType' | 'resourceName'>): string | null {
  if (!r.entityId) return null;
  if (r.resourceType === 'cluster') return `/clusters/${encodeURIComponent(r.resourceName)}`;
  if (r.resourceType === 'secret') return `/secrets/${encodeURIComponent(r.resourceName)}`;
  return null;
}
